import React, { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import Callout from '../ui/Callout';
import { useT, useLocale } from '../../i18n/LocaleContext';
import { formatCurrency } from '../../utils/format';
import { providerLabel } from './constants';

const MIN_AMOUNT = 100;

/** Step 1 of a payout: amount + note. The destination is never editable here. */
export default function PayoutRequestModal({ isOpen, onClose, onSubmit, overview }) {
  const t = useT();
  const { locale } = useLocale();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setAmount('');
    setNote('');
    setError('');
  }, [isOpen]);

  const available = overview?.available || 0;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const value = Number(amount);
    if (!amount || !Number.isInteger(value)) {
      setError(t('payouts_page.amount_integer'));
      return;
    }
    if (value < MIN_AMOUNT) {
      setError(t('payouts_page.amount_min', { min: formatCurrency(MIN_AMOUNT, locale) }));
      return;
    }
    if (value > available) {
      setError(t('payouts_page.amount_exceeds', { available: formatCurrency(available, locale) }));
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({ amount: value, note: note.trim() });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={submitting ? () => {} : onClose}
      title={t('payouts_page.new_payout')}
      subtitle={t('payouts_page.new_payout_sub')}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="gold" onClick={handleSubmit} loading={submitting}>{t('otp.send_code')}</Button>
        </>
      )}
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="payout-target">
          <div>
            <div className="detail-item-label">{t('payouts_page.destination_title')}</div>
            <div className="payout-target__number">{overview?.payoutMsisdn}</div>
            <div className="payout-target__meta">
              {overview?.payoutName} · {providerLabel(t, overview?.payoutProviderId)}
            </div>
          </div>
          <div className="payout-target__balance">
            <div className="detail-item-label">{t('payouts_page.available')}</div>
            <div className="payout-target__amount">{formatCurrency(available, locale)}</div>
          </div>
        </div>

        <FormField label={t('payouts_page.field_amount')} htmlFor="payout-amount" required error={error}>
          <Input
            id="payout-amount"
            type="number"
            min={MIN_AMOUNT}
            step={1}
            inputMode="numeric"
            value={amount}
            onChange={(e) => { setAmount(e.target.value); if (error) setError(''); }}
            invalid={Boolean(error)}
            autoFocus
          />
        </FormField>
        <FormField label={t('payouts_page.field_note')} htmlFor="payout-note" hint={t('common.optional')}>
          <Textarea id="payout-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={2} />
        </FormField>

        <Callout tone="warning" title={t('payouts_page.confirm_heading')}>
          <ul className="callout__list">
            <li>{t('payouts_page.confirm_only_number', { number: overview?.payoutMsisdn || '' })}</li>
            <li>{t('payouts_page.confirm_email_code')}</li>
            <li>{t('payouts_page.confirm_xentripay_otp')}</li>
            <li>{t('payouts_page.confirm_estimate')}</li>
          </ul>
        </Callout>
      </form>
    </Modal>
  );
}
